import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, Observable, Subscriber, Subscription, of, throwError } from 'rxjs';
import { catchError, delay, mergeMap, retryWhen } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { AuthSessionServiceDelegate } from '../../../authentication/services/delegate.auth.session.service';
import { SessionService } from '../../administration/services/session.service';
import { LogService } from './log.service';
import {ToastrService} from 'ngx-toastr';
import { AccessibleToastComponent } from '../../../components/toast/accessible-toast.component';

export interface IHttpOptions {
  basePath?: string;
  XPath?: string;
  httpParams?: HttpParams | { [param: string]: string | string[] };
  httpHeaders?: HttpHeaders;
  httpResponseType?: 'json' | 'text' | 'blob';
  f?: Function;
  error?: Function;
  complete?: Function;
  redo?: number;
}

export class HttpOptions implements IHttpOptions {
  basePath?: string;
  XPath?: string;
  httpHeaders?: HttpHeaders;
  httpParams?: HttpParams | { [param: string]: string | string[] };
  httpResponseType?: 'json' | 'text' | 'blob';
  f?: Function;
  error?: Function;
  complete?: Function;
  redo?: number;

  constructor() {
    this.basePath = environment.piste_base_url;
    this.httpHeaders = new HttpHeaders().set('Content-Type', 'application/json');
    this.redo = 0;
  }
}

const HttpOptionsDefaults: HttpOptions = {
  basePath: environment.piste_base_url,
  httpHeaders: new HttpHeaders().set('Content-Type', 'application/json'),
  redo: 0,
  httpParams: new HttpParams,
  httpResponseType: 'json',
  f: undefined,
  error: undefined,
  complete: undefined
};

@Injectable({
  providedIn: 'root'
})
export class HttpService {

  private LOG: LogService = new LogService(HttpService);
  private headers = new HttpHeaders().set('Content-Type', 'application/json');
  private options: HttpOptions;

  constructor(public httpClient: HttpClient, private sessionService: SessionService, 
    private authSessionServiceDelegate:AuthSessionServiceDelegate, private toastr: ToastrService) {
  }

  public get<T, U>(XPath: string, t?: new () => T | U, params?: HttpOptions): Observable<T> {
    this.options = { ...HttpOptionsDefaults, ...params };
    this.options.XPath = XPath;
    if (this.options.httpResponseType == 'json') {
      return this.process(this.httpClient.get<T>(
        this.options.basePath + XPath,
        {
          headers: this.options.httpHeaders,
          responseType: 'json',
          params: this.options.httpParams
        }
      ), t, this.options);
    }
  }

  public getText(XPath: string, t?: new () => ArrayBuffer | string | Blob, params?: HttpOptions): Observable<ArrayBuffer | string | Blob> {
    this.options = { ...HttpOptionsDefaults, ...params };
    this.options.XPath = XPath;
    if (this.options.httpResponseType == 'text') {
      return this.process(this.httpClient.get(
        this.options.basePath + XPath,
        {
          headers: this.options.httpHeaders,
          params: this.options.httpParams,
          responseType: this.options.httpResponseType
        }
      ), t, this.options);
    }
  }

  public getBlob<T, U>(XPath: string,  t?: new () => T | U, params?: HttpOptions): Observable<T> {
    this.options = { ...HttpOptionsDefaults, ...params };
    this.options.XPath = XPath;
    return this.process(this.httpClient.get<T>(
      this.options.basePath + XPath,
      {
        headers: this.options.httpHeaders,
        params: this.options.httpParams,
        responseType: 'arraybuffer' as 'json'
      }
    ), t, this.options);
  }

  public post<T, U>(XPath: string, json: any,  t?: new () => T | U, params?: HttpOptions): Observable<T> {
    this.options = { ...HttpOptionsDefaults, ...params };
    this.options.XPath = XPath;
    return this.process(this.httpClient.post<T>(
      this.options.basePath + XPath, json,
      {
        headers: this.options.httpHeaders,
        params: this.options.httpParams
      }
    ), t, this.options);
  }

  public postBlob<T, U>(XPath: string, json: any,  t?: new () => T | U, params?: HttpOptions): Observable<T> {
    this.options = { ...HttpOptionsDefaults, ...params };
    this.options.XPath = XPath;
    return this.process(this.httpClient.post<T>(
      this.options.basePath + XPath, json,
      {
        headers: this.options.httpHeaders,
        params: this.options.httpParams,
        responseType: 'arraybuffer' as 'json'
      }
    ), t, this.options);
  }
  public postMultipart<T, U>(XPath: string, json: any,  t?: new () => T | U, params?: HttpOptions): Observable<T> {
    this.options = { ...HttpOptionsDefaults, ...params };
    this.options.XPath = XPath;
    return this.process(this.httpClient.post<T>(
      this.options.basePath + XPath, json,
      {
        headers: new HttpHeaders(),
        params: this.options.httpParams
      }
    ), t, this.options);
  }

  public put<T, U>(XPath: string, json: any,  t?: new () => T | U, params?: HttpOptions): Observable<T> {
    this.options = { ...HttpOptionsDefaults, ...params };
    this.options.XPath = XPath;
    return this.process(this.httpClient.put<T>(
      this.options.basePath + XPath, json,
      {
        headers: this.headers,
        params: this.options.httpParams
      }
    ), t, this.options);
  }

  public patch<T, U>(XPath: string, json: any,  t?: new () => T | U, params?: HttpOptions): Observable<T> {
    this.options = { ...HttpOptionsDefaults, ...params };
    this.options.XPath = XPath;
    return this.process(this.httpClient.patch<T>(
      this.options.basePath + XPath, json,
      {
        headers: this.headers,
        params: this.options.httpParams
      }
    ), t, this.options);
  }

  public delete<T, U>(XPath: string,  t?: new () => T | U, params?: HttpOptions): Observable<T> {
    this.options = { ...HttpOptionsDefaults, ...params };
    this.options.XPath = XPath;
    return this.process(this.httpClient.delete<T>(
      this.options.basePath + XPath,
      {
        headers: this.headers,
        params: this.options.httpParams
      }
    ), t, this.options);
  }

  public request<T, U>(method: string, XPath: string, json: any,  t?: new () => T | U, params?: HttpOptions): Observable<T> {
    this.options = { ...HttpOptionsDefaults, ...params };
    this.options.XPath = XPath;
    return this.process<T, U>(this.httpClient.request<T>(
      method, this.options.basePath + XPath,
      {
        body: json,
        headers: this.headers,
        params: this.options.httpParams
      }
    ), t, this.options);
  }

  /**
   * transforme une structure JSON en objet instancié ou en tableau d'objets via le constructeur de la classe choisie
   * @type T: type de donnée en entrée/sortie
   * @type U: type des éléments si T est un tableau
   * @param plainObject donnée brute récupérée (donnée JSON)
   * @param classObject structure à utiliser pour instancier l'objet ou les éléments
   * @returns objet ou tableau instancié
   */
  // FIXME Do a *deep* instanciation. This only deal with the first hiearchy of object. Use structuredClone when available.
  public instanciate<T, U>(plainObject: T, classObject?: new () => T | U): T {
    if (!classObject) return plainObject;

    if (Array.isArray(plainObject)) {
      var objectTable = (plainObject as unknown as Array<U>).map(element => Object.assign(new classObject(), element) as U)
      return objectTable as unknown as T;
    } else {
      return Object.assign(new classObject(), plainObject);
    }
  }

  private process<T, U>(request: Observable<T>, t?: new () => T | U, params?: HttpOptions): Observable<T> {
    const ret = new Observable<T>((observer) => {
      let redo = params.redo ? params.redo : 0;
      let s = request
        .pipe(
          retryWhen(
            (errors) => errors.pipe(
              delay(10000),
              mergeMap(error => {
                return redo-- > 0 ?
                  of(error) :
                  throwError(error)
              })
            )
          ),
          catchError((error) => {
            this.errorProcessor(error, params, 1);
            return EMPTY;
          })
        )

        .subscribe(
          (data) => {
            if (data instanceof HttpErrorResponse) {
              this.errorProcessor(data.toString(), params, 2);
              observer.error(data);
            }
            if (params.f) {
              data = params.f(data);
            }
            observer.next(this.instanciate(data, t));
            s.unsubscribe();
          },
          (err) => {
            if (params.error) {
              params.error(err);
            }
            this.errorProcessor(err, params, 3);
            observer.error(err);
          },
          () => {
            observer.complete();
          }
        );
      this.unsubscribe(s, observer, params);
    });
    return ret;
  }

  private async unsubscribe<T>(subscribe: Subscription, observer: Subscriber<T>, params: HttpOptions) {
    await new Promise(
      () => setTimeout(() => {
        if (!subscribe.closed) {
          this.errorProcessor('La requête n\'a pas reçu de réponse à temps : ', params, 0);
          observer.error('La requête n\'a pas reçu de réponse à temps : ');
          subscribe.unsubscribe();
        }
      }, 300000));
  }

  private errorProcessor(error, params: HttpOptions, callMethod: number) {
    this.LOG.error('erreur '+ callMethod + ' lors de l\'appel à ' + params.basePath + params.XPath, error);

    if (error instanceof HttpErrorResponse && error.status === 401) {
      this.sessionService.clear();
      this.authSessionServiceDelegate.logOut();
    } else {
      this.displayErrorMessage();
    }
  }

  /**
   * Open a toaster and display an error messsage.
   */
  private displayErrorMessage() {
    const title = 'Erreur';
    let message = 'Une erreur est survenue, si le problème persiste, merci de contacter le support';
    if (message !== null) {
      this.toastr.show(message, title, {
        positionClass: 'toast-top-right',
        toastComponent: AccessibleToastComponent
      },
        'error');
    }
  }
}
